<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Exchange, ExchangeItem as ExchangeItemType } from '@/types/shared/exchange'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_PRICE_TYPE,
} from '@/types/shared/player-config'
import { useConfig } from '@/composables/game/config'
import { formatUnshortenedNumber } from '@/helpers/utils'
import ThePremium from '@/components/ThePremium.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ExchangeItem from '@/components/exchange/ExchangeItem.vue'
import { useConfetti } from '@/composables/game/confetti'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'
import { twMerge } from 'tailwind-merge'
import { usePlayer } from '@/composables/game/player'
import { getExchangeRewardDisplay } from '@/composables/game/exchange-rewards'

const props = defineProps<{
  show: boolean
  item: ExchangeItemType
  itemId: string
  exchange: Exchange
}>()

const emit = defineEmits<{
  (e: 'hide'): void
  (e: 'purchaseDone'): void
}>()

const { config } = useConfig()
const { showConfetti } = useConfetti()
const { goldenCoinValue } = usePlayer()

const itemRewardDisplay = computed(() => {
  return getExchangeRewardDisplay(props.item.reward, goldenCoinValue.value)
})

const maybeEcommerceItem = computed(() => {
  return config.value.game.ecommerce.items.find(item =>
    item.benefits.some(b =>
      b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.EXCHANGE_ITEM &&
      'itemId' in b &&
      b.itemId === props.itemId,
    ),
  )
})

const ecommerceItem = computed(() => {
  if (!maybeEcommerceItem.value) {
    throw new Error('Ecommerce item not found for exchange item: ' + props.itemId)
  }
  return maybeEcommerceItem.value
})

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(ecommerceItem.value)

const isLoading = ref(false)
const purchased = ref(false)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const paid = await onPurchase(priceType, { type: 'exchange', itemId: props.itemId })
    if (paid) {
      await showConfetti()
      purchased.value = true
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}

function onClose() {
  if (purchased.value) {
    emit('purchaseDone')
  }
  emit('hide')
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="pt-6"
    bg-fade-class="!z-[804]"
    wrapper-class="!z-[804]"
    @hide="onClose"
  >
    <div class="relative w-full pt-7">
      <StarsBalancePill
        class="absolute left-0 top-0"
        :style="'dark'"
      />
      <ExchangeItem
        :class="twMerge('mx-auto scale-[1.15] pointer-events-none')"
        :item-id="itemId"
        :item="item"
        hide-cost
      />
      <Transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="purchased"
          class="mt-10 flex w-full flex-col items-center"
        >
          <div class="text-[#FFFEFD] text-[24px] leading-8 font-bold font-pilat">
            Purchase successful!
          </div>
          <div
            v-if="itemRewardDisplay"
            class="text-center text-base font-medium text-white/60"
          >
            {{ itemRewardDisplay.amount }} {{ itemRewardDisplay.title }} =
            ${{ starPrice?.usdAmount?.toFixed(2) || '0.00' }}
          </div>
          <BaseButton
            class="mt-6 w-full"
            @click="onClose"
          >
            Close
          </BaseButton>
        </div>
        <div
          v-else
          class="mt-10 flex w-full flex-col items-center"
        >
          <div
            v-if="itemRewardDisplay"
            class="text-center text-[24px] leading-8 font-bold font-pilat text-[#FFFEFD]"
          >
            {{ itemRewardDisplay.amount }} {{ itemRewardDisplay.title }} =
            ${{ starPrice?.usdAmount?.toFixed(2) || '0.00' }}
          </div>

          <div class="mt-6 flex w-full flex-col gap-y-3">
            <BaseButton
              class="w-full"
              :loading="isBuyingWithStars"
              @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
            >
              <span
                v-if="!isBuyingWithStars"
                class="flex items-center gap-x-2"
              >
                Buy with
                <ThePremium
                  :with-tooltip="false"
                  image-class="size-5 min-w-[20px]"
                />
                <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice?.amount || 0) }}</span>
              </span>
              <span v-else>
                Processing...
              </span>
            </BaseButton>

            <template v-if="showTonPurchase">
              <BaseButton
                style-type="secondary"
                :loading="isBuyingWithTon"
                class="relative w-full"
                @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
              >
                <template #outerContent>
                  <TonDiscountBadge label="-10%" />
                </template>
                <span
                  v-if="!isBuyingWithTon"
                  class="flex items-center gap-x-2"
                >
                  Buy with
                  <img
                    src="@/assets/images/ton-symbol.png"
                    class="size-5 min-w-[20px] object-cover"
                    alt="Ton symbol"
                  >
                  <span>{{ tonPrice?.amount }}</span>
                </span>
                <span v-else>
                  Processing...
                </span>
              </BaseButton>

              <div class="text-center font-exo text-sm font-semibold text-gunmetal-default">
                Transaction may take a few minutes to process. You'll be notified once it's successful.
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </TheBottomSheet>
</template>
